import React, { useEffect, useRef, useState } from "react";
import { Select, Spin } from "antd";
import { SearchOutlined } from "@ant-design/icons";

import { HttpMethod, initFetch } from "../../utils/fetcher";
import { proprietaireToAdapted } from "../../adapter/proprietaireAdapter";
import { locataireToAdapted } from "../../adapter/locataireAdapter";
import { lotToAdapted } from "../../adapter/lotAdapter";

const DEBOUNCE_TIMEOUT = 300;

// Libellé affiché pour chaque type de résultat renvoyé par search/multi-criteres
const labelsByType = {
  personnes_proprietaires: ({ code, personne }) =>
    `PROPRIETAIRE - ${code} ${personne.nom} ${personne.prenom}`,
  lots: ({ reference }) => `LOT - ${reference}`,
  personnes_locataires: ({ code, personne }) =>
    `LOCATAIRE - ${code} ${personne.nom} ${personne.prenom}`,
};

const toOptions = (results) =>
  results
    .filter((item) => labelsByType[item.type])
    .map((item) => ({
      value: `${item.type}-${item.searchable.id}`,
      label: labelsByType[item.type](item.searchable),
      item,
    }));

const searchMultiCriteres = (value) => {
  const { url, params } = initFetch(
    `search/multi-criteres?q=${encodeURIComponent(value)}`,
    HttpMethod.GET
  );
  return fetch(url, params).then((res) => {
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  });
};

// Recherche multi-critères (propriétaires, lots, locataires) : modèle antd 5
// « Search and Select Users » (select/demo/select-users), debounce sans lodash.
const SearchInput = ({ addPanesPersonne, addPanesLot, placeholder, style }) => {
  const [searchValue, setSearchValue] = useState("");
  const [options, setOptions] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState(false);
  const timeoutRef = useRef(null);
  // Identifiant de la dernière recherche : ignore les réponses arrivées dans le désordre
  const fetchIdRef = useRef(0);

  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  const handleSearch = (value) => {
    setSearchValue(value);
    clearTimeout(timeoutRef.current);
    fetchIdRef.current += 1;
    const fetchId = fetchIdRef.current;

    setOptions([]);
    setError(false);

    if (!value) {
      setFetching(false);
      return;
    }

    setFetching(true);
    timeoutRef.current = setTimeout(() => {
      searchMultiCriteres(value)
        .then((results) => {
          if (fetchId === fetchIdRef.current) setOptions(toOptions(results));
        })
        .catch(() => {
          if (fetchId === fetchIdRef.current) setError(true);
        })
        .finally(() => {
          if (fetchId === fetchIdRef.current) setFetching(false);
        });
    }, DEBOUNCE_TIMEOUT);
  };

  const handleSelect = (value, option) => {
    const { type, searchable } = option.item;

    switch (type) {
      case "personnes_proprietaires":
        addPanesPersonne(proprietaireToAdapted(searchable));
        break;
      case "lots":
        addPanesLot(lotToAdapted(searchable));
        break;
      case "personnes_locataires":
        addPanesPersonne(locataireToAdapted(searchable), false);
        break;
      default:
        break;
    }

    // La sélection ouvre un onglet : le champ est remis à zéro
    handleSearch("");
  };

  let notFoundContent = null;
  if (fetching) notFoundContent = <Spin size="small" />;
  else if (error) notFoundContent = "Recherche indisponible";
  else if (searchValue) notFoundContent = "Aucun résultat";

  return (
    <Select
      showSearch
      size="large"
      variant="underlined"
      value={null}
      searchValue={searchValue}
      placeholder={placeholder}
      style={style}
      prefix={<SearchOutlined />}
      suffixIcon={null}
      defaultActiveFirstOption={false}
      filterOption={false}
      onSearch={handleSearch}
      onSelect={handleSelect}
      notFoundContent={notFoundContent}
      options={options}
    />
  );
};

export default SearchInput;
